// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Route } from '../types';
import { esc, pctv, compact, money, num, flag, countryColour, shortName, fmtValue } from '../format';
import { gloss } from '../glossary';
import { navigate } from '../main';

export function renderOverview(root: HTMLElement, d: Dataset, _route: Route): void {
  const c = d.meta.counts;
  const order = d.meta.classOrder;

  const cards = order.map((cid) => {
    const t = c.classTop[cid];
    if (!t) return '';
    const col = countryColour(t.iso, t.name);
    const valStr = cid === 'commercial' && t.dollars != null ? `${num(t.value)} sites · ${money(t.dollars)}`
      : cid === 'residential' ? `${num(t.value)} buyers`
      : fmtValue(cid, t.value);
    return `
      <button class="flip-card" data-country="${esc(t.name)}" style="border-top-color:${col}" aria-label="Open ${esc(t.name)}">
        <div class="fc-class">${esc(d.meta.classMeta[cid].label)}</div>
        <div class="fc-country"><span class="cdot" style="background:${col}"></span><span class="flagemoji">${flag(t.iso)}</span><span class="nm">${esc(shortName(t.name))}</span></div>
        <div class="fc-val">${valStr} · ${pctv(t.share * 100, 0)} of foreign-held</div>
      </button>`;
  }).join('');

  const distinct = new Set(order.map((cid) => c.classTop[cid]?.name).filter(Boolean)).size;

  root.innerHTML = `
    <div class="hero">
      <div class="kicker">ATO Register of Foreign Ownership · at 30 June ${esc(String(c.latestYear))}</div>
      <h2>Who owns Australia? It depends <span class="big">entirely on what you count.</span></h2>
      <p>Foreign investors hold a stake in about <strong>${pctv(c.agForeignSharePct)}</strong> of the country's farmland and <strong>${pctv(c.waterForeignSharePct)}</strong> of its water. But there is no single biggest foreign owner: <strong>${distinct} different countries</strong> lead the six asset classes. The ${esc(shortName(c.agTop?.name ?? ''))} tops farmland, ${esc(shortName(c.classTop.commercial?.name ?? ''))} commercial property and mining, ${esc(shortName(c.classTop.residential?.name ?? ''))} home purchases, and a tax-haven fund domicile leads business interests.</p>
    </div>

    <div class="stat-row">
      <div class="stat"><div class="stat-label">Farmland foreign-held</div><div class="stat-value">${pctv(c.agForeignSharePct)}</div><div class="stat-note">${gloss('levelOfOwnership', 'level of ownership')} · ${compact(c.agForeignHa)} ha</div></div>
      <div class="stat"><div class="stat-label">Water foreign-held</div><div class="stat-value">${pctv(c.waterForeignSharePct)}</div><div class="stat-note">${compact(c.waterForeignML / 1000)} GL of entitlements</div></div>
      <div class="stat"><div class="stat-label">Commercial property value</div><div class="stat-value">${money(c.commercialValueTotal)}</div><div class="stat-note">foreign-registered sites</div></div>
      <div class="stat hl"><div class="stat-label">China's farmland vs peak</div><div class="stat-value">${c.agChinaDropPct != null ? pctv(c.agChinaDropPct * 100, 0) : '—'}</div><div class="stat-note">now ${compact(c.agChinaNow)} ha — a sell-down, not a buy-up</div></div>
      <div class="stat"><div class="stat-label">Source countries</div><div class="stat-value">${num(c.nCountries)}</div><div class="stat-note">across six asset classes</div></div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h3>The biggest foreign owner of each asset class</h3>
        <p>Every class has a different leader — the heart of why "foreign ownership of Australia" can't be reduced to one number or one country. Click any card to open that country.</p>
      </div>
      <div class="flip-grid">${cards}</div>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>Foreign vs Australian share of registered farmland</h3>
      <p>Even where the Register records a foreign interest, much of the same land can be Australian-owned — a foreign interest is a substantial stake, not full ownership.</p></div>
      ${shareBar(d)}
    </div>

    <div class="note-band">
      <strong>Read this first.</strong> "Source country" is the country of <em>control</em> — where the controlling person or fund manager sits — not where a company is incorporated, which is why fund domiciles like the Cayman Islands appear. The six classes use different units (hectares, gigalitres, dollars, counts) and cannot be added together. Open <button data-goto="atlas" class="lnk">The Atlas</button> to see the full country-by-asset picture.
    </div>
  `;

  root.querySelectorAll<HTMLElement>('.flip-card').forEach((el) =>
    el.addEventListener('click', () => navigate({ view: 'atlas', country: el.dataset.country })));
  root.querySelector<HTMLElement>('[data-goto="atlas"]')?.addEventListener('click', () => navigate({ view: 'atlas' }));
  // style the inline link
  const lnk = root.querySelector<HTMLElement>('.lnk');
  if (lnk) { lnk.style.cssText = 'background:none;border:0;padding:0;color:var(--accent-primary);font:inherit;text-decoration:underline;cursor:pointer'; }
}

function shareBar(d: Dataset): string {
  const lu = d.classes.agricultural.landUse;
  if (!lu || !lu.length) return '';
  const now = lu[lu.length - 1];
  const total = now.total ?? 1;
  const f = (now.foreign ?? 0) / total, a = (now.aust ?? 0) / total;
  return `
    <div style="display:flex;height:34px;border-radius:6px;overflow:hidden;border:1px solid var(--border-subtle)">
      <div data-tip="Foreign share of registered parcels: ${compact(now.foreign)} ha (${pctv(f * 100, 0)})" style="width:${f * 100}%;background:var(--accent-warm);display:flex;align-items:center;justify-content:center;color:#fff;font-size:var(--font-size-sm)">Foreign ${pctv(f * 100, 0)}</div>
      <div data-tip="Australian share within the same registered parcels: ${compact(now.aust)} ha (${pctv(a * 100, 0)})" style="width:${a * 100}%;background:var(--accent-primary);display:flex;align-items:center;justify-content:center;color:#fff;font-size:var(--font-size-sm)">Australian ${pctv(a * 100, 0)}</div>
    </div>
    <p class="mini-note">Within the ${compact(total)} ha of agricultural land that carries any registered foreign interest, at 30 June ${d.meta.latestYear}. Nationally these parcels are ${pctv((d.meta.counts.agForeignSharePct ?? 0))} of all Australian farmland.</p>`;
}
